/**
 * Toast Notification Component
 * Replaces browser alert() with beautiful, non-blocking notifications
 *
 * Usage:
 * import { showToast } from './components/Toast';
 * showToast.success("Operation successful!");
 * showToast.error("Something went wrong");
 * showToast.warning("Please review your input");
 * showToast.info("New message received");
 */

import React, { createContext, useContext, useState, useCallback } from "react";
import "./Toast.css";

const ToastContext = createContext();

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = "info", duration = 4000) => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type, duration };

    setToasts((prev) => [...prev, newToast]);

    // Auto-dismiss after duration
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const success = useCallback(
    (message, duration) => showToast(message, "success", duration),
    [showToast],
  );
  const error = useCallback(
    (message, duration) => showToast(message, "error", duration),
    [showToast],
  );
  const warning = useCallback(
    (message, duration) => showToast(message, "warning", duration),
    [showToast],
  );
  const info = useCallback(
    (message, duration) => showToast(message, "info", duration),
    [showToast],
  );

  return (
    <ToastContext.Provider
      value={{ showToast, removeToast, success, error, warning, info }}
    >
      {children}
      <div className="toast-container">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            {...toast}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const Toast = ({ message, type, onClose }) => {
  const icons = {
    success: "✓",
    error: "✕",
    warning: "⚠",
    info: "ℹ",
  };

  return (
    <div className={`toast toast-${type}`}>
      <div className="toast-icon">{icons[type]}</div>
      <div className="toast-message">{message}</div>
      <button
        className="toast-close"
        onClick={onClose}
        aria-label="Close notification"
      >
        ×
      </button>
    </div>
  );
};

// Hook to use toast in components
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
};

// Global instance for use outside React components
let globalToastContext = null;

export const initializeToast = (context) => {
  globalToastContext = context;
};

export const showToast = {
  success: (message, duration) =>
    globalToastContext?.success(message, duration),
  error: (message, duration) => globalToastContext?.error(message, duration),
  warning: (message, duration) =>
    globalToastContext?.warning(message, duration),
  info: (message, duration) => globalToastContext?.info(message, duration),
};

export default ToastProvider;
